import { memo } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import OutputCard from "./OutputCard";
import PraiseLogo from "@/components/PraiseLogo";
import { User } from "lucide-react";

export interface PlatformOutput {
  platform: string;
  content: string;
}

export interface AgentMsg {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachments?: { type: string; data: string; name?: string }[];
  platformOutputs?: PlatformOutput[];
  timestamp: Date;
}

/** Parse ---PLATFORM:xxx--- blocks from agent text */
export function parsePlatformOutputs(text: string): { cleanText: string; outputs: PlatformOutput[] } {
  const outputs: PlatformOutput[] = [];
  const regex = /---PLATFORM:(\w+)---\n?([\s\S]*?)---END_PLATFORM---/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    outputs.push({ platform: match[1], content: match[2].trim() });
  }
  const cleanText = text.replace(regex, "").trim();
  return { cleanText, outputs };
}

interface AgentMessageProps {
  message: AgentMsg;
}

const AgentMessage = memo(({ message }: AgentMessageProps) => {
  const isUser = message.role === "user";

  let displayText = message.content;
  let outputs = message.platformOutputs || [];
  if (!isUser && !message.platformOutputs) {
    const parsed = parsePlatformOutputs(message.content);
    displayText = parsed.cleanText;
    outputs = parsed.outputs;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
    >
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
        isUser
          ? "bg-muted"
          : "ink-border ink-wash"
      }`}>
        {isUser ? (
          <User className="h-4 w-4 text-muted-foreground" />
        ) : (
          <PraiseLogo size={18} />
        )}
      </div>

      {/* Content */}
      <div className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${isUser ? "items-end" : "items-start"}`}>
        {/* Image attachments */}
        {message.attachments?.map((att, i) =>
          att.type === "image" ? (
            <img key={i} src={att.data} alt={att.name || "Upload"} className="rounded-xl max-h-48 mb-2 ink-border" />
          ) : null
        )}

        {/* Text bubble */}
        {displayText && (
          <div
            className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              isUser
                ? "bg-foreground text-background rounded-br-sm"
                : "bg-muted text-foreground rounded-bl-sm"
            }`}
          >
            {isUser ? (
              <p className="whitespace-pre-wrap">{displayText}</p>
            ) : (
              <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-headings:my-2">
                <ReactMarkdown>{displayText}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* Platform output cards */}
        {outputs.length > 0 && (
          <div className="mt-3 w-full space-y-2">
            {outputs.map((output, i) => (
              <OutputCard key={`${output.platform}-${i}`} platform={output.platform} content={output.content} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
});

AgentMessage.displayName = "AgentMessage";

export default AgentMessage;
